"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { categoryMeta, getCategoryColor } from "./data";
import type { ResearchPost } from "./types";
import {
  CategoryBadge as SharedCategoryBadge,
  PostListItem as SharedPostListItem,
  HeroFeaturedCard as SharedHeroFeaturedCard,
  ArchiveNewsletterForm,
} from "@/components/sections/editorial/_shared/archive/parts";

export function HeroVisual({ color }: { color: string }) {
  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: "#0d0d0f" }}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 560 360"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <defs>
          <pattern id="rsg-sm" width="28" height="28" patternUnits="userSpaceOnUse">
            <path
              d="M 28 0 L 0 0 0 28"
              fill="none"
              stroke="white"
              strokeWidth="0.3"
              strokeOpacity="0.07"
            />
          </pattern>
          <pattern id="rsg-lg" width="112" height="112" patternUnits="userSpaceOnUse">
            <rect width="112" height="112" fill="url(#rsg-sm)" />
            <path
              d="M 112 0 L 0 0 0 112"
              fill="none"
              stroke="white"
              strokeWidth="0.6"
              strokeOpacity="0.10"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#rsg-lg)" />
        <rect x="60" y="180" width="32" height="140" fill={color} opacity="0.12" rx="3" />
        <rect x="60" y="220" width="32" height="100" fill={color} opacity="0.55" rx="3" />
        <rect x="120" y="140" width="32" height="180" fill={color} opacity="0.12" rx="3" />
        <rect x="120" y="170" width="32" height="150" fill={color} opacity="0.55" rx="3" />
        <rect x="180" y="100" width="32" height="220" fill={color} opacity="0.12" rx="3" />
        <rect x="180" y="120" width="32" height="200" fill={color} opacity="0.55" rx="3" />
        <rect x="240" y="60" width="32" height="260" fill={color} opacity="0.12" rx="3" />
        <rect x="240" y="80" width="32" height="240" fill={color} opacity="0.75" rx="3" />
        <rect x="300" y="90" width="32" height="230" fill={color} opacity="0.12" rx="3" />
        <rect x="300" y="110" width="32" height="210" fill={color} opacity="0.55" rx="3" />
        <rect x="360" y="130" width="32" height="190" fill={color} opacity="0.12" rx="3" />
        <rect x="360" y="150" width="32" height="170" fill={color} opacity="0.45" rx="3" />
        <rect x="420" y="160" width="32" height="160" fill={color} opacity="0.12" rx="3" />
        <rect x="420" y="190" width="32" height="130" fill={color} opacity="0.35" rx="3" />
        <path
          d="M 76 310 C 136 280 196 260 256 200 C 316 140 376 200 436 240"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
        />
        <line
          x1="40"
          y1="20"
          x2="40"
          y2="330"
          stroke="white"
          strokeWidth="0.6"
          strokeOpacity="0.10"
        />
        <line
          x1="40"
          y1="330"
          x2="520"
          y2="330"
          stroke="white"
          strokeWidth="0.6"
          strokeOpacity="0.10"
        />
        <circle cx="256" cy="200" r="4" fill={color} opacity="0.9" />
        <circle cx="256" cy="200" r="10" fill={color} opacity="0.15" />
      </svg>
      <div className="text-foreground/[18%] absolute right-7 bottom-6 flex items-center gap-2.5 select-none">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d="M11 2 L19.5 7 L19.5 15 L11 20 L2.5 15 L2.5 7 Z"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
          <text
            x="11"
            y="15"
            textAnchor="middle"
            fontSize="9"
            fontWeight="700"
            fill="currentColor"
            fontFamily="sans-serif"
          >
            P
          </text>
        </svg>
        <span className="text-sm font-semibold tracking-wide">Proxidize</span>
      </div>
      <div
        className="absolute"
        style={{
          left: "38%",
          top: "10%",
          width: 160,
          height: 120,
          background: `radial-gradient(ellipse at center, ${color}22, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export function CategoryBadge({ category }: { category: string }) {
  const color = getCategoryColor(category);
  const icon = categoryMeta[category]?.icon;
  return <SharedCategoryBadge category={category} color={color} icon={icon} />;
}

export function PostListItem({ post, index }: { post: ResearchPost; index: number }) {
  const color = getCategoryColor(post.category);
  return (
    <SharedPostListItem
      post={post}
      index={index}
      color={color}
      readMoreLabel="Read report"
      ns="research"
    />
  );
}

export function HeroFeaturedCard({ post }: { post: ResearchPost }) {
  const color = getCategoryColor(post.category);
  return (
    <SharedHeroFeaturedCard
      post={post}
      color={color}
      readMoreLabel="Read report"
      visual={<HeroVisual color={color} />}
      categoryBadge={<CategoryBadge category={post.category} />}
    />
  );
}

export function ResearchArchiveNewsletterForm() {
  return <ArchiveNewsletterForm ariaLabel="Research newsletter signup" />;
}

export { Pagination } from "@/components/ui/pagination";

export function CategoryFilterSidebar({
  categories,
  activeCategory,
  onCategoryChange,
}: {
  categories: string[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
}) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <div className="bg-card border-border overflow-hidden rounded-xl border">
      <div className="border-border border-b px-5 py-4">
        <h3 className="text-foreground text-lg font-semibold">Categories</h3>
      </div>
      <ul className="space-y-1 px-5 py-3">
        {categories.map((cat, i) => {
          const isActive = activeCategory === cat;
          const icon = cat !== "All" ? categoryMeta[cat]?.icon : null;
          return (
            <motion.li
              key={cat}
              initial={prefersReducedMotion ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.25 + i * 0.04, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={() => onCategoryChange(cat)}
                aria-pressed={isActive}
                style={{ ["--cat-color" as string]: "var(--brand-green)" }}
                className={
                  "group relative flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium " +
                  "cursor-pointer transition-all duration-200 ease-out " +
                  "focus-visible:ring-2 focus-visible:ring-[color:var(--cat-color)]/40 focus-visible:outline-none " +
                  (isActive
                    ? "bg-[color:var(--cat-color)]/[0.12] text-[color:var(--cat-color)]"
                    : "text-muted-foreground hover:translate-x-0.5 hover:bg-[color:var(--cat-color)]/[0.06] hover:text-[color:var(--cat-color)]")
                }
              >
                <span className="flex items-center gap-2">
                  <span
                    className={
                      "inline-flex items-center justify-center transition-colors duration-200 " +
                      (isActive
                        ? "text-[color:var(--cat-color)]"
                        : "text-muted-foreground group-hover:text-[color:var(--cat-color)]")
                    }
                  >
                    {icon}
                  </span>
                  {cat}
                </span>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

export function CategoryFilterSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="bg-card border-border overflow-hidden rounded-xl border"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="border-border border-b px-5 py-4">
        <h3 className="text-foreground text-lg font-semibold">Categories</h3>
      </div>
      <ul className="space-y-1 px-5 py-3">
        {/* eslint-disable react/no-array-index-key -- skeleton loader rows have no stable IDs */}
        {Array.from({ length: rows }).map((_, i) => (
          <li key={`cat-skeleton-${i}`} className="px-3 py-2">
            <span className="flex items-center gap-2">
              <span className="bg-foreground/6 h-3 w-3 animate-pulse rounded-sm" />
              <span
                className="bg-foreground/6 h-3 animate-pulse rounded"
                style={{ width: `${55 + ((i * 13) % 35)}%` }}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ResearchPostsLoader({ label = "Loading research" }: { label?: string }) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center gap-4"
    >
      <div
        className={prefersReducedMotion ? "h-12 w-12" : "h-12 w-12 animate-spin"}
        style={prefersReducedMotion ? undefined : { animationDuration: "0.9s" }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 48 48" fill="none" className="h-full w-full">
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="color-mix(in srgb, var(--foreground) 8%, transparent)"
            strokeWidth="3"
          />
          <path
            d="M24 4 a20 20 0 0 1 20 20"
            stroke="var(--brand-green)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </div>

      <div className="flex flex-col items-center gap-2">
        <p className="text-foreground/80 text-sm font-medium">{label}</p>
        {!prefersReducedMotion && (
          <div className="flex items-center gap-1">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="block h-1 w-1 rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
